import DOMPurify from 'dompurify';

/**
 * Sanitize HTML string to prevent XSS attacks
 * 
 * @param {string} dirty - The potentially unsafe HTML string
 * @param {object} config - Optional DOMPurify configuration
 * @returns {string} - The sanitized HTML string
 * 
 * @example
 * const clean = sanitizeHTML('<script>alert("xss")</script><p>Hello</p>');
 * // Returns: '<p>Hello</p>'
 */
export const sanitizeHTML = (dirty, config = {}) => {
    // Default configuration for common use cases
    const defaultConfig = {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'span', 'div', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
        ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'id', 'style'],
        ALLOW_DATA_ATTR: false,
        ...config
    };

    return DOMPurify.sanitize(dirty || '', defaultConfig);
};

/**
 * Sanitize HTML with custom configuration for specific use cases
 */

// For basic text with inline formatting only
export const sanitizeInlineHTML = (dirty) => {
    return sanitizeHTML(dirty, {
        ALLOWED_TAGS: ['strong', 'em', 'u', 'span', 'br'],
        ALLOWED_ATTR: ['class']
    });
};

// For rich text content with links
export const sanitizeRichHTML = (dirty) => {
    return sanitizeHTML(dirty, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'span', 'div', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'code', 'pre'],
        ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'id', 'style'],
        ALLOW_DATA_ATTR: false
    });
};

// For simple text with HTML entities only (like the clock emoji use case)
export const sanitizeSimpleHTML = (dirty) => {
    return sanitizeHTML(dirty, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
        KEEP_CONTENT: true,
        ALLOW_DATA_ATTR: false
    });
};

/**
 * Safely set innerHTML of an element
 * This is a utility function for cases where you must use innerHTML directly
 * 
 * @param {HTMLElement} element - The DOM element
 * @param {string} html - The HTML string to set
 * @param {object} config - Optional DOMPurify configuration
 * 
 * @example
 * const element = document.querySelector('.my-element');
 * safeSetInnerHTML(element, userContent);
 */
export const safeSetInnerHTML = (element, html, config = {}) => {
    if (!element) return;
    
    const sanitized = sanitizeHTML(html, config);
    element.innerHTML = sanitized;
};

/**
 * Parse HTML string and return text content only (strips all HTML tags)
 * 
 * @param {string} html - The HTML string
 * @returns {string} - The text content without HTML tags
 */
export const stripHTML = (html) => {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    return doc.documentElement.textContent || '';
};

export default sanitizeHTML;
